'use client'

import { useState } from 'react'
import { Sparkles, TrendingUp } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import {
  CaseAnalysis,
  CaseGoals,
  CasePrescription,
  SERVICE_TYPE_LABEL,
  SERVICE_TYPE_TO_FLOW,
  toMockCaseRecord,
  toMockHoldings,
  toMockSteps,
  toMockTrades,
  useCaseGoals,
  useCaseHoldings,
  useCasePrescriptionSteps,
  useCaseTrades,
  useMyCaseForFlow,
  useSetMyPrescriptionStepDone,
  type FlowType,
} from '@/features/cases'
import { useMyInvestorCases } from '@/features/investors'
import { AuditHero } from '@/features/investor/components/home/audit-hero'
import { NotRequestedCard } from '@/features/investor/components/home/not-requested-card'
import { PendingCaseCard } from '@/features/investor/components/home/pending-case-card'
import { ServiceUpsell } from '@/features/investor/components/home/service-upsell'
import { TradesHero } from '@/features/investor/components/home/trades-hero'
import { InvestorShell } from '@/features/investor/components/investor-shell'
import { useMyRecommendations, useUpdateRecommendationStatus } from '@/features/recommendations'
import { queryKeys } from '@/lib/queryKeys'

const FLOW_TAB_LABEL: Record<FlowType, string> = {
  audit: 'Portfolio',
  trades: 'Trades',
  build: 'Goals',
}

const FLOW_SECTION_COPY: Record<'audit' | 'trades', { title: string; description: string }> = {
  audit: {
    title: 'Your Holdings',
    description: 'Every scheme in your CAS, with allocation and gain/loss.',
  },
  trades: {
    title: 'Your Trades',
    description: 'Every trade, read straight from your tradebook.',
  },
}

/**
 * Ported from `nivex-design/apps/nivex-web-v3/src/pages/dashboard/DashboardHome.tsx`'s
 * `DashboardHomeContent` — one hero card per owned, published service, a pending/not-requested
 * card otherwise, plus a cross-sell row for services not yet owned. Includes all 3 flows
 * (`audit`/`trades`/`build`) — Build renders the same read-only `CaseGoals` plus the same
 * `CasePrescription` step list the dedicated `/dashboard/build` view uses, since it has no
 * hero/analysis-card equivalent of its own.
 */
export function InvestorDashboardView() {
  const { data: investorCases = [] } = useMyInvestorCases()
  const { data: recommendations = [] } = useMyRecommendations()
  const updateRecommendationStatus = useUpdateRecommendationStatus(queryKeys.recommendations.mine)
  const [tab, setTab] = useState<FlowType>('audit')

  const query = useMyCaseForFlow(tab)
  const holdingsQuery = useCaseHoldings(tab !== 'build' ? query.data?.id : undefined)
  const tradesQuery = useCaseTrades(tab !== 'build' ? query.data?.id : undefined, 1, 50)
  const stepsQuery = useCasePrescriptionSteps(query.data?.id)
  const goalsQuery = useCaseGoals(tab === 'build' ? query.data?.id : undefined)
  const setStepDone = useSetMyPrescriptionStepDone(query.data?.id ?? '')
  const advisorName = query.summary?.advisorName ?? null
  const ownedFlows = new Set(investorCases.map((c) => SERVICE_TYPE_TO_FLOW[c.serviceType]))
  const topRecommendations = recommendations
    .filter((r) => r.status === 'open')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 2)

  return (
    <InvestorShell active="home">
      <div className="flex flex-col gap-4">
        {/* Sticky so the flow tabs stay put while the case content below scrolls — the negative
            margins cancel the shell's own padding so the bar spans edge-to-edge once stuck, then
            the matching positive padding puts that space back for its own content. */}
        <div className="bg-background sticky top-0 z-10 -mx-4 -mt-4 flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-4 md:-mt-6 md:pt-6 lg:-mx-6 lg:px-6">
          <Tabs value={tab} onValueChange={(v) => setTab(v as FlowType)}>
            <TabsList>
              {(Object.keys(FLOW_TAB_LABEL) as FlowType[]).map((flow) => (
                <TabsTrigger key={flow} value={flow}>
                  {FLOW_TAB_LABEL[flow]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {advisorName ? (
            <Badge variant="secondary" className="hidden max-w-56 truncate sm:inline-flex">
              {advisorName} is looking after this
            </Badge>
          ) : null}

          {/* Content scrolling in from underneath dissolves out here instead of hitting the bar's
              solid background as a hard-clipped line. */}
          <div
            aria-hidden="true"
            className="from-background pointer-events-none absolute inset-x-0 -bottom-6 h-6 bg-gradient-to-b to-transparent"
          />
        </div>

        {query.isListLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !query.summary ? (
          <NotRequestedCard flow={tab} />
        ) : query.summary.caseStatus !== 'published' ? (
          // Detail is never fetched for a not-yet-published case (see `useMyCaseForFlow`), so
          // checking `query.summary.caseStatus` here — not `query.data`/`query.isLoading` — is
          // what lets this render instead of an infinitely-loading skeleton.
          <PendingCaseCard
            caseRecord={{ id: query.summary.id, flow: tab, status: query.summary.caseStatus }}
          />
        ) : query.isLoading || !query.data ? (
          <Skeleton className="h-40 w-full" />
        ) : tab === 'build' ? (
          goalsQuery.isError ? (
            <QueryErrorState error={goalsQuery.error} onRetry={() => void goalsQuery.refetch()} />
          ) : goalsQuery.isLoading || !goalsQuery.data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="flex flex-col gap-5">
              <CaseGoals
                caseId={query.data.id}
                goals={goalsQuery.data}
                buildProfile={query.data.buildProfile}
                investorAge={query.data.investor?.age ?? null}
              />
              <div className="border-t pt-5">
                {stepsQuery.isError ? (
                  <QueryErrorState
                    error={stepsQuery.error}
                    onRetry={() => void stepsQuery.refetch()}
                  />
                ) : stepsQuery.isLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : (
                  <CasePrescription
                    caseRecord={toMockCaseRecord(query.data)}
                    injectedSteps={stepsQuery.data ? toMockSteps(stepsQuery.data) : []}
                    onToggleStepDone={(id, done) => setStepDone.mutate({ stepId: id, done })}
                  />
                )}
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-4">
            {tab === 'audit' && (
              <AuditHero
                caseRecord={toMockCaseRecord(query.data)}
                injectedHoldings={
                  holdingsQuery.data ? toMockHoldings(holdingsQuery.data, query.data.id) : []
                }
                injectedSteps={stepsQuery.data ? toMockSteps(stepsQuery.data) : []}
              />
            )}
            {tab === 'trades' && (
              <TradesHero
                caseRecord={toMockCaseRecord(query.data)}
                injectedTrades={
                  tradesQuery.data ? toMockTrades(tradesQuery.data.items, query.data.id) : []
                }
              />
            )}
            <Card>
              <CardHeader className="flex flex-row items-start gap-3 space-y-0">
                <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
                  <TrendingUp className="size-5" />
                </span>
                <div>
                  <CardTitle className="text-base font-medium">
                    {FLOW_SECTION_COPY[tab].title}
                  </CardTitle>
                  <CardDescription>{FLOW_SECTION_COPY[tab].description}</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <CaseAnalysis
                  caseRecord={toMockCaseRecord(query.data)}
                  hideSummary
                  injectedHoldings={
                    holdingsQuery.data ? toMockHoldings(holdingsQuery.data, query.data.id) : []
                  }
                  injectedTrades={
                    tradesQuery.data ? toMockTrades(tradesQuery.data.items, query.data.id) : []
                  }
                />
              </CardContent>
            </Card>
          </div>
        )}

        {topRecommendations.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center gap-2 space-y-0">
              <Sparkles className="text-primary size-4" />
              <CardTitle className="text-base font-medium">Recommended for you</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {topRecommendations.map((rec) => (
                <div key={rec.id} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{SERVICE_TYPE_LABEL[rec.serviceType]}</p>
                    <p className="text-muted-foreground truncate text-xs">{rec.reason}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      size="sm"
                      onClick={() =>
                        updateRecommendationStatus.mutate({ id: rec.id, status: 'accepted' })
                      }
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        updateRecommendationStatus.mutate({ id: rec.id, status: 'dismissed' })
                      }
                    >
                      Not now
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <ServiceUpsell ownedFlows={ownedFlows} />
      </div>
    </InvestorShell>
  )
}
